// includes/js-src/shared/pl-browser-pricing.ts
//
// PLT (an encounter's Power Level Total) for LIST views — shared by the adventure page and the catalog-table
// lists (My encounters, a location's encounters). PL is priced ONLY in the browser (Janus C2b / C2d): the server
// sums the STORED browser PL its compile served and lists every row with none as PENDING (template id per row,
// multiplicity kept — ft_encounter_creature_totals() → ft_encounter_pl_pending_split()). This module prices those
// pending templates with the core bundle's PL engine and turns a row's stored sum + pending list into a total —
// or `null` while any row is still unpriced, so a page shows the `ft-pl-pending` treatment, never a partial sum.
//
//   * signed-in viewer → window.ftC2SweepBatch: prices AND posts the heal claims, so the next view serves the stored value;
//   * anonymous viewer → window.ftPlPriceIds: read-only (pl_claim_inc.php requires a session).
// A row the definition pricer cannot price (a player character: `pendingOther`), a refusal, or an absent engine keeps
// the total pending.

export interface PlPriceRow { id: string; pl: number | null; refusal?: string }
export interface PlSweepAnswer { per_id?: Record<string, { outcome?: string; value?: unknown; token?: string }>; error?: string }
export interface PlHooks {
	ftPlPriceIds?: (ids: string[], onRow: (r: PlPriceRow) => void, chunk?: number) => Promise<unknown>;
	ftC2SweepBatch?: (ids: string[], perClaimBudgetMs?: number) => Promise<PlSweepAnswer>;
}

/** One encounter row's PLT inputs as the server shipped them. */
export interface PlTotalInput {
	/** Σ (int) STORED browser PL over the rows that have one. */
	stored: number;
	/** Template id of every row with no stored browser PL (one entry per row). */
	pendingIds?: readonly string[];
	/** Pending rows the definition pricer cannot price (a player character). */
	pendingOther?: number;
	/** Named failures from this page's browser pricing attempt. */
	pendingReasons?: readonly string[];
}

export type PlState = 'ready' | 'pricing' | 'pending';

export const PL_PRICE_CHUNK = 8;
/** Nonblocking list pricing must hydrate a cold closure as well as price it.
 * Measured valid Shaman dependency adoption exceeds the action lane's eight seconds. */
export const PL_LIST_CLAIM_BUDGET_MS = 20000;
export const PL_HOOK_WAIT_MS = 15000;
/** Sort key for a pending total: non-numeric, so it is never read as 0. */
export const PL_PENDING_SORT = 'pending';

export function plPendingCount(input: PlTotalInput): number {
	return (input.pendingIds?.length ?? 0) + (input.pendingOther ?? 0);
}

/** Can this row's pending rows be priced here at all (i.e. none is a PC)? */
export function plPriceable(input: PlTotalInput): boolean {
	return (input.pendingIds?.length ?? 0) > 0 && (input.pendingOther ?? 0) === 0;
}

/** The total once every pending row has a browser price; `null` while any is missing (never a partial sum). */
export function plTotalOf(input: PlTotalInput, priced: ReadonlyMap<string, number>): number | null {
	if ((input.pendingOther ?? 0) > 0) return null;
	let total = input.stored;
	for (const id of input.pendingIds ?? []) {
		const value = priced.get(id.toUpperCase());
		if (value === undefined) return null;
		total += Math.trunc(value); // per ROW, truncated like the server's (int) pl_total() sum
	}
	return total;
}

/** Hover text for a pending total. */
export function plPendingTip(input: PlTotalInput, state: PlState): string {
	const n = plPendingCount(input);
	const why = state === 'pricing'
		? `pending: pricing ${n} creature${n === 1 ? '' : 's'} in your browser`
		: input.pendingReasons?.length ? `unavailable: ${input.pendingReasons.join('; ')}` : `pending: ${n} creature${n === 1 ? '' : 's'} not priced yet`;
	return `Power Level: ${why}`;
}

/** The distinct (upper-cased) template ids to price across `inputs`. */
export function plDistinctPendingIds(inputs: readonly PlTotalInput[]): string[] {
	return Array.from(new Set(inputs.flatMap((input) => (input.pendingIds ?? []).map((id) => id.toUpperCase()))));
}

/** Wait (bounded) for the core bundle's PL engine to install its window hooks. `null` when it never does. */
export async function waitForPlHooks(timeoutMs = PL_HOOK_WAIT_MS): Promise<PlHooks | null> {
	const hooks = window as unknown as PlHooks;
	const deadline = Date.now() + timeoutMs;
	while (Date.now() < deadline) {
		if (typeof hooks.ftPlPriceIds === 'function' || typeof hooks.ftC2SweepBatch === 'function') return hooks;
		await new Promise((resolve) => setTimeout(resolve, 250));
	}
	return null;
}

/** Price `ids` (definition roots) in the browser, `PL_PRICE_CHUNK` at a time, reporting each chunk's values. Never throws. */
export async function priceDefinitionsInBrowser(hooks: PlHooks, ids: string[], persist: boolean, onChunk: (values: Map<string, number>, refusals: Map<string, string>) => void, chunkSize = PL_PRICE_CHUNK): Promise<void> {
	for (let i = 0; i < ids.length; i += chunkSize) {
		const part = ids.slice(i, i + chunkSize);
		const values = new Map<string, number>();
		const refusals = new Map<string, string>();
		// a batch-level failure only fills ids with no outcome yet: it never masks a price or a more specific refusal
		const fail = (reason: string): void => { for (const id of part) if (!values.has(id.toUpperCase()) && !refusals.has(id.toUpperCase())) refusals.set(id.toUpperCase(), reason); };
		try {
			if (persist && typeof hooks.ftC2SweepBatch === 'function') {
				// prices AND posts the heal claims: the stash fills, so the server serves the stored browser PL next time
                const answer = await hooks.ftC2SweepBatch(part, PL_LIST_CLAIM_BUDGET_MS);
				if (answer?.error) fail(/abort|timeout/i.test(answer.error) ? 'definition-fetch-timeout' : answer.error);
				for (const [id, r] of Object.entries(answer?.per_id ?? {})) {
					if (r && r.outcome === 'priced' && typeof r.value === 'number' && Number.isFinite(r.value)) { values.set(id.toUpperCase(), r.value); refusals.delete(id.toUpperCase()); }
					else refusals.set(id.toUpperCase(), r?.token || r?.outcome || 'definition-price-unavailable');
				}
			} else if (typeof hooks.ftPlPriceIds === 'function') {
				await hooks.ftPlPriceIds(part, (r) => {
					if (r && typeof r.pl === 'number' && Number.isFinite(r.pl)) values.set(String(r.id).toUpperCase(), r.pl);
					else if (r) refusals.set(String(r.id).toUpperCase(), r.refusal || 'definition-price-unavailable');
				}, PL_PRICE_CHUNK);
			}
		} catch (error) {
			fail(error instanceof Error && /abort|timeout/i.test(error.message) ? 'definition-fetch-timeout' : 'definition-pricing-error');
		}
		for (const id of part) if (!values.has(id.toUpperCase()) && !refusals.has(id.toUpperCase())) refusals.set(id.toUpperCase(), 'definition-price-unavailable');
		onChunk(values, refusals);
	}
}
